import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  MoreVert,
  CheckCircle,
  Edit,
  Delete,
  EventBusy,
  Repeat,
} from '@mui/icons-material'
import { useState } from 'react'
import { format } from 'date-fns'
import type { Task } from '@/types'
import { useFamilyStore } from '@/store/useFamilyStore'
import { useTaskStore } from '@/store/useTaskStore'
import { useAuthStore } from '@/store/useAuthStore'

interface TaskCardProps {
  task: Task
  date?: Date
  onEdit: (task: Task) => void
  onComplete?: (task: Task) => void
}

export default function TaskCard({ task, date, onEdit, onComplete }: TaskCardProps) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const members = useFamilyStore((state) => state.members)
  const deleteTask = useTaskStore((state) => state.deleteTask)
  const skipTask = useTaskStore((state) => state.skipTask)
  const currentUserId = useAuthStore((state) => state.currentUserId)

  const assignedMember = members.find((m) => m.id === task.assigned_to)

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(e.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      await deleteTask(task.id)
    }
    handleMenuClose()
  }

  const handleSkip = async () => {
    if (date) {
      await skipTask(task.id, format(date, 'yyyy-MM-dd'))
    }
    handleMenuClose()
  }

  const handleComplete = () => {
    if (onComplete) {
      onComplete(task)
    }
    handleMenuClose()
  }

  const getPriorityColor = () => {
    switch (task.priority) {
      case 'High':
        return 'error'
      case 'Medium':
        return 'warning'
      case 'Low':
        return 'info'
      default:
        return 'default'
    }
  }

  const getPriorityGradient = () => {
    switch (task.priority) {
      case 'High':
        return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
      case 'Medium':
        return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
      case 'Low':
        return 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
      default:
        return 'linear-gradient(135deg, #64748b 0%, #475569 100%)'
    }
  }

  return (
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: getPriorityGradient(),
        },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="flex-start" gap={2.5}>
          {assignedMember && (
            <Avatar
              src={assignedMember.avatar_url || undefined}
              sx={{
                width: 48,
                height: 48,
                bgcolor: assignedMember.color,
                fontSize: '1.125rem',
                border: '3px solid',
                borderColor: `${assignedMember.color}22`,
                boxShadow: `0 4px 12px ${assignedMember.color}33`,
              }}
            >
              {assignedMember.name[0]}
            </Avatar>
          )}

          <Box flex={1}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: '1.125rem',
                mb: 1.5,
              }}
            >
              {task.title}
            </Typography>

            <Box display="flex" gap={1} flexWrap="wrap" mb={1.5}>
              <Chip
                label={task.room}
                size="small"
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
              <Chip
                label={task.task_type}
                size="small"
                sx={{
                  bgcolor: 'secondary.main',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
              <Chip
                label={task.priority}
                size="small"
                color={getPriorityColor()}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
              {task.recurrence_rule && (
                <Chip
                  icon={<Repeat sx={{ fontSize: '0.9rem !important' }} />}
                  label="Recurring"
                  size="small"
                  sx={{
                    bgcolor: 'info.main',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                />
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                mb: task.notes ? 1 : 0,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                ⏱️ {task.estimated_duration} min
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                👤 {assignedMember?.name}
              </Typography>
              {date && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  📅 {format(date, 'PPp')}
                </Typography>
              )}
            </Box>

            {task.notes && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mt: 1.5,
                  p: 1.5,
                  bgcolor: 'rgba(0,0,0,0.02)',
                  borderRadius: 2,
                  borderLeft: '3px solid',
                  borderColor: 'primary.main',
                }}
              >
                {task.notes}
              </Typography>
            )}
          </Box>

          <IconButton
            size="medium"
            onClick={handleMenuOpen}
            sx={{
              bgcolor: 'rgba(0,0,0,0.04)',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.08)',
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <MoreVert />
          </IconButton>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
          >
            {onComplete && task.assigned_to === currentUserId && (
              <MenuItem onClick={handleComplete}>
                <ListItemIcon>
                  <CheckCircle fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText>Mark Complete</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={() => { onEdit(task); handleMenuClose(); }}>
              <ListItemIcon>
                <Edit fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
            {date && task.recurrence_rule && (
              <MenuItem onClick={handleSkip}>
                <ListItemIcon>
                  <EventBusy fontSize="small" color="warning" />
                </ListItemIcon>
                <ListItemText>Skip This Occurrence</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleDelete}>
              <ListItemIcon>
                <Delete fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete Task</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </CardContent>
    </Card>
  )
}
